import { useCallback, useState } from "react";
import { toast } from "~/components/ui/toaster";
import { resizeImageForUpload } from "~/lib/imageResize";

interface UseImageUploadOptions<T> {
  /** Function to upload the resized image blob */
  uploadFn: (file: Blob) => Promise<T>;
  /** Called on successful upload */
  onSuccess?: (result: T) => void;
  /** Called on upload error */
  onError?: (error: Error) => void;
}

interface UseImageUploadResult<T> {
  /** Upload a file (resizes automatically before upload) */
  upload: (file: File) => Promise<T | null>;
  /** True while an upload is in progress */
  uploading: boolean;
}

/**
 * Hook for uploading images with automatic client-side resize.
 * Handles loading state and error notifications.
 */
export function useImageUpload<T>({ uploadFn, onSuccess, onError }: UseImageUploadOptions<T>): UseImageUploadResult<T> {
  const [uploading, setUploading] = useState(false);

  const upload = useCallback(
    async (file: File): Promise<T | null> => {
      setUploading(true);

      try {
        const resized = await resizeImageForUpload(file);
        const result = await uploadFn(resized);
        onSuccess?.(result);
        return result;
      } catch (e) {
        const error = e instanceof Error ? e : new Error("Upload failed");
        onError?.(error);
        toast.error("Failed to upload image");
        return null;
      } finally {
        setUploading(false);
      }
    },
    [uploadFn, onSuccess, onError],
  );

  return { upload, uploading };
}
